import { useState, useMemo, useCallback, useLayoutEffect } from 'react'

import { Disease, Form, UsePreConditionsToggleReturn } from '../../../ana-toolbox'
import { DiseaseType } from '../../../ana-toolbox'
import { scrollToElementIfNotVisible } from '../helpers/scrollToElementIfNotVisible'
import { LATIN_CHARACTERS_ERROR } from '../yup/latinValidation'
import { latinDiseasesSchema } from '../yup/yup'

const getInitialCollapseStates = (diseases: Disease[], initialValues: Disease[]): Record<string, boolean> => {
  const selectedDiseases: Disease[] = [...diseases, ...initialValues].filter((disease) =>
    initialValues.some((disease_) => disease_.type === disease.type)
  )

  const categoriesInUse: string[] = selectedDiseases.map((disease) => disease.type)

  return Object.fromEntries(categoriesInUse.map((category) => [category, true]))
}

const isChecked = ([_, checked]: [string, boolean]) => checked
const takeFirst = <T = unknown>([first]: T[]) => first

const removeError = (errors: ErrorByDiseaseType, diseaseType: DiseaseType) => ({
  ...errors,
  [diseaseType]: undefined,
})

const addError = (errors: ErrorByDiseaseType, diseaseType: DiseaseType, error: string) => ({
  ...errors,
  [diseaseType]: error,
})

const getErrors = (selectedDiseases: Disease[], collapseStates: Record<string, boolean>) => {
  const openDiseases = Object.entries(collapseStates).filter(isChecked).map(takeFirst) as DiseaseType[]

  const errors = openDiseases.reduce<Partial<Record<DiseaseType, string>>>((newErrors, diseaseType) => {
    const diseasesOfType = selectedDiseases.filter((disease) => disease.type === diseaseType)

    if (!diseasesOfType.length) return addError(newErrors, diseaseType, 'disease_required')
    if (!latinDiseasesSchema.isValidSync(diseasesOfType))
      return addError(newErrors, diseaseType, LATIN_CHARACTERS_ERROR)

    return removeError(newErrors, diseaseType)
  }, {})

  return errors
}

type UsePreConditionsToggleInput = {
  initialValues: Partial<Form>
  diseases: Disease[]
  onChange?: (diseases: Disease[]) => void
}

type ByDiseaseType<T> = Partial<Record<DiseaseType, T>>
type ErrorByDiseaseType = ByDiseaseType<string>

export const usePreConditionsLogic = ({
  initialValues,
  diseases,
  onChange,
}: UsePreConditionsToggleInput): UsePreConditionsToggleReturn => {
  const [collapseStates, setCollapseStates] = useState<Record<string, boolean>>(
    getInitialCollapseStates(diseases, initialValues.diseases || [])
  )

  const [selectedDiseases, setSelectedDiseases] = useState<Disease[]>(initialValues.diseases || [])
  const [submitCount, setSubmitCount] = useState(0)
  const [errorPerSubmitCount, setErrorPerSubmitCount] = useState<
    Record<number, { type: DiseaseType; error: string } | undefined>
  >({})

  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<{ name: string }>, checked: boolean) => {
      const diseaseType = event.currentTarget.name as DiseaseType

      setCollapseStates((previousValue) => ({ ...previousValue, [diseaseType]: checked }))

      if (!checked) {
        const uncheckedDiseases = (previousValue: Disease[]) =>
          previousValue.filter((disease) => disease.type !== diseaseType)

        setSelectedDiseases((previous) => {
          const newValues = uncheckedDiseases(previous)

          onChange?.(newValues)

          return newValues
        })
      }
    },
    [setCollapseStates, setSelectedDiseases, onChange]
  )

  const handleDiseaseCheck = useCallback(
    (d: Disease[]) => {
      setErrorPerSubmitCount((previous) => ({
        ...previous,
        [submitCount]: undefined,
      }))

      setSelectedDiseases(d)
      onChange?.(d)
    },
    [setSelectedDiseases, submitCount, setErrorPerSubmitCount, onChange]
  )

  const errors = useMemo(() => getErrors(selectedDiseases, collapseStates), [selectedDiseases, collapseStates])

  const hasError = useMemo(() => Object.entries(errors).some(([_, error]) => error !== undefined), [errors])

  const handleDiseaseChange = useCallback(
    (_: unknown, d: Disease[]) => {
      setSelectedDiseases(d)
      onChange?.(d)

      setErrorPerSubmitCount((previous) => ({
        ...previous,
        [submitCount]: undefined,
      }))
    },
    [setSelectedDiseases, submitCount, setErrorPerSubmitCount, onChange]
  )

  const handleSubmit = useCallback(() => {
    const firstDiseaseWithError: Disease | undefined = diseases.find((disease) => Boolean(errors[disease.type]))
    const errorMessage = firstDiseaseWithError ? errors[firstDiseaseWithError.type] : undefined

    let error: { type: DiseaseType; error: string } | undefined

    if (firstDiseaseWithError && errorMessage) {
      error = { type: firstDiseaseWithError.type, error: errorMessage }
    }

    if (hasError) {
      error = Object.entries<string>(errors as Record<string, string>)
        .map(([type, e]) => ({ type: type as DiseaseType, error: e }))
        .find(({ error: e }) => e !== undefined)
    }

    const newCount = submitCount + 1

    setSubmitCount(newCount)
    setErrorPerSubmitCount((previous) => ({ ...previous, [newCount]: error }))
  }, [setSubmitCount, submitCount, errors, diseases, hasError])

  /* This field is used for lazy validation, it represents the first disease with
   * error, this way we ensure that the validation will be displayed in the correct order.
   */
  const nextError = useMemo(() => {
    return errorPerSubmitCount[submitCount]
  }, [errorPerSubmitCount, submitCount])

  useLayoutEffect(() => {
    if (nextError) {
      setTimeout(() => {
        scrollToElementIfNotVisible(`[data-disease-type="${nextError?.type}"]`)
      }, 100)
    }
  }, [nextError])

  return {
    collapseStates,
    selectedDiseases,
    errors,
    hasError,
    nextError,
    handleToggleChange,
    handleDiseaseCheck,
    handleDiseaseChange,
    handleSubmit,
  }
}
